var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-531 — the "Výchozí hodnota" control for a subfield whose input is an
 * authority record picker (e.g. 370 `$c` → GEOGRAPHIC).
 *
 * Deliberately the SAME control the cataloguer meets in the form: a Stripes
 * `Selection` fed by `useRecordPickerOptions`, the hook `RecordPickerSubfield`
 * uses. Two reasons, and only the second is cosmetic:
 *
 *   1. The admin must be choosing from the very set the form will offer. A
 *      datalist over some other query would be a different list that merely
 *      looks authoritative.
 *   2. It closes half of the side observation on CZNA-531 — the settings
 *      suggest-box looking unlike the form's picker. (Only half: the
 *      VOCABULARY branch still renders a datalist. Unifying that is its own
 *      change and is not attempted here.)
 *
 * A pick writes BOTH the heading and the record id (see
 * `SubfieldDef.defaultValueSourceAuthorityId`), because on a picker a value is
 * a reference. Storing the heading alone would pre-fill text that links
 * nowhere. Since CZNA-798 the form does render such a heading (as an unlinked
 * option) rather than showing an empty field, so the default is at least visible
 * — but it is still a reference to nothing, which is why a pick writes both.
 *
 * Two things this control refuses to do:
 *
 *   - **Offer pending proposals.** `useRecordPickerOptions` merges
 *     PENDING_REVIEW / CHANGES_REQUESTED proposals into the picker for
 *     GEOGRAPHIC (CZNA-197) because the backend re-links a proposal reference
 *     when the proposal is published. That re-link rewrites records and
 *     proposals — not form configs. A proposal id parked in a form config would
 *     therefore dangle forever, so proposals are filtered out here.
 *   - **Blank a stored value it cannot resolve.** A default typed as free text
 *     before this ticket has no FK; it is shown as an explicitly flagged option
 *     rather than dropped, so the admin sees what is there and decides. Silent
 *     erasure of NK's stored config is the failure mode that made CZNA-483
 *     expensive.
 */
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Selection, IconButton } from '@folio/stripes/components';
import useRecordPickerOptions from '../../hooks/useRecordPickerOptions';
import { AUTHORITY_RECORD_TYPES } from './utils/subfieldInputKind';
/**
 * Sentinel for a stored default that carries no record id, so `Selection` has
 * an option to render instead of falling back to the placeholder. Mirrors
 * `NO_FK_VALUE_PREFIX` in `RecordPickerSubfield` — same problem, same
 * shape — and can never collide with a UUID.
 */
var UNLINKED_PREFIX = '__unlinked__';
var HINT_STYLE = {
    fontSize: '0.75rem',
    color: '#666',
    marginTop: '0.2rem',
};
var AuthorityDefaultValueEditor = function (_a) {
    var _b, _c;
    var subfield = _a.subfield, recordType = _a.recordType, onChange = _a.onChange, testId = _a["data-testid"];
    var intl = useIntl();
    var _d = useState(false), typed = _d[0], setTyped = _d[1];
    var current = (_b = subfield.defaultValue) !== null && _b !== void 0 ? _b : '';
    var currentId = (_c = subfield.defaultValueSourceAuthorityId) !== null && _c !== void 0 ? _c : '';
    var isUnlinked = current !== '' && currentId === '';
    var selectionValue = currentId || (current ? "".concat(UNLINKED_PREFIX).concat(current) : '');
    var _e = useRecordPickerOptions({ recordType: recordType }), searchOptions = _e.dataOptions, loading = _e.loading, handleFilter = _e.handleFilter;
    var dataOptions = useMemo(function () {
        // Proposals are not addressable from a form config — see the file header.
        var records = searchOptions.filter(function (o) { return !o.isProposal; });
        if (!current || records.some(function (o) { return o.value === selectionValue; }))
            return records;
        return __spreadArray([
            {
                value: selectionValue,
                label: isUnlinked
                    ? intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.defaultValue.recordUnlinkedOption' }, { value: current })
                    : current,
                heading: current,
            }
        ], records, true);
    }, [searchOptions, current, selectionValue, isUnlinked, intl]);
    var onFilter = function (text) {
        setTyped(text.trim().length > 0);
        handleFilter(text);
    };
    var handleSelectionChange = function (id) {
        // Re-picking the sentinel is a no-op: it is a rendering device, not a
        // record, and treating it as a pick would write the prefix into the config.
        if (id === selectionValue)
            return;
        var opt = dataOptions.find(function (o) { return o.value === id; });
        if (opt)
            onChange(opt.heading, opt.value);
    };
    return (React.createElement("div", null,
        React.createElement("div", { style: { display: 'flex', gap: '4px', alignItems: 'flex-end' } },
            React.createElement("div", { style: { flex: 1 } },
                React.createElement(Selection, { label: intl.formatMessage({ id: 'ui-czna-authorities.settings.forms.visual.property.defaultValue' }), dataOptions: dataOptions, value: selectionValue, onChange: handleSelectionChange, onFilter: onFilter, asyncFilter: true, loading: loading, placeholder: intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.search' }), "data-testid": testId })),
            current !== '' && (React.createElement(IconButton, { icon: "times-circle-solid", onClick: function () { return onChange(undefined, undefined); }, "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.form.clearField' }), "data-testid": "".concat(testId, "-clear") }))),
        !typed && (React.createElement("div", { style: HINT_STYLE, "data-testid": "".concat(testId, "-record-hint") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.recordHint", 
                // Only the documented picker targets have a translated name; an
                // out-of-list `recordType` (a hand-edited config) shows its raw
                // code rather than a missing-translation id.
                values: {
                    recordType: AUTHORITY_RECORD_TYPES.includes(recordType)
                        ? intl.formatMessage({ id: "ui-czna-authorities.settings.forms.visual.property.recordType.".concat(recordType) })
                        : recordType,
                } }))),
        isUnlinked && (React.createElement("div", { style: __assign(__assign({}, HINT_STYLE), { color: '#a06000' }), "data-testid": "".concat(testId, "-record-unlinked") },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.settings.forms.visual.property.defaultValue.recordUnlinked", values: { value: current } })))));
};
export default AuthorityDefaultValueEditor;
